import {
  Flex,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Button,
  Skeleton,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { FinancialData } from '@finley-technologies/finley-types';

import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { useGetFinancialStatementDataByDateRangeQuery } from '~/packages/financial-data/requests/financialData.gql.generated';
import { FinancialStatementsChart } from '~/packages/financial-data/ui/financialStatements/FinancialStatementsChart';
import { FinancialStatementsDatePicker } from '~/packages/financial-data/ui/financialStatements/FinancialStatementsDatePicker';
import {
  FinancialStatementsTable,
  getFinancialStatementsChartAndTableProps,
} from '~/packages/financial-data/ui/financialStatements/FinancialStatementsTable';
import {
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';

const REPORT_FREQUENCY_OPTIONS: {
  [key in FinancialData.ReportFrequency]: string;
} = {
  month: 'M',
  quarter: 'Q',
  year: 'Y',
};

export const FinancialStatementsContainer = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });
  const onSelectDateRange = (newDateRange: [Date | null, Date | null]) => {
    const [newStartDate, newEndDate] = newDateRange;

    if (newStartDate && newEndDate) {
      setChartDataDateRange({
        chartDataStartDate: newStartDate,
        chartDataEndDate: newEndDate,
      });
    }

    if (!newStartDate && !newEndDate) {
      setChartDataDateRange({
        chartDataStartDate: chartsInitialStartDateJs,
        chartDataEndDate: chartsInitialEndDateJs,
      });
    }
  };

  const [
    selectedReportFrequency,
    setSelectedReportFrequency,
  ] = useState<FinancialData.ReportFrequency>('month');

  const [selectedSectionName, setSelectedSectionName] = useState<
    | FinancialData.BalanceSheetSectionTypes
    | FinancialData.IncomeStatementSectionTypes
  >('assets');

  const [
    selectedFinancialStatement,
    setSelectedFinancialStatement,
  ] = useState<FinancialData.FinancialStatementType>('balanceSheet');

  const { user } = useUserAuthOrThrow();

  const financialSectionType =
    selectedFinancialStatement === 'balanceSheet'
      ? FinancialData.BalanceSheetSectionTypeIterable
      : FinancialData.IncomeStatementSectionTypeIterable;

  const financialStatementProps = useMemo(
    () =>
      getFinancialStatementsChartAndTableProps(
        selectedFinancialStatement,
        selectedReportFrequency,
        user.organizationId,
        chartDataDateRange,
        selectedSectionName,
        creditFacilityId,
      ),
    [
      selectedFinancialStatement,
      selectedReportFrequency,
      user.organizationId,
      chartDataDateRange,
      selectedSectionName,
      creditFacilityId,
    ],
  );

  const financialStatementQueryResult = useGetFinancialStatementDataByDateRangeQuery(
    {
      variables: financialStatementProps,
    },
  );

  if (financialStatementQueryResult.loading) {
    return <Skeleton mt="2" h="100px" />;
  }

  if (financialStatementQueryResult.error)
    throw financialStatementQueryResult.error;

  return (
    <Flex direction="column" gap="1rem">
      <Typography variant="section-title" trim>
        Financial Statements
      </Typography>
      <Flex justifyContent="space-between" flexWrap="wrap" gap="0.5rem">
        <Flex gap="0.5rem">
          <FinancialStatementsDatePicker
            onSelectDateRange={onSelectDateRange}
            currentDateRange={chartDataDateRange}
            width="400px"
          />

          {FinancialData.ReportFrequencyIterable.map((reportFrequency) => {
            const isSelected = reportFrequency === selectedReportFrequency;
            return (
              <Button
                key={reportFrequency}
                onClick={() => setSelectedReportFrequency(reportFrequency)}
                isActive={isSelected}
                variant="primary-muted"
              >
                {REPORT_FREQUENCY_OPTIONS[reportFrequency]}
              </Button>
            );
          })}
        </Flex>

        <Flex gap="0.5rem">
          {financialSectionType.map((sectionName) => {
            const isSelected = selectedSectionName === sectionName;
            return (
              <Button
                key={sectionName}
                onClick={() => setSelectedSectionName(sectionName)}
                isActive={isSelected}
                variant="primary-muted"
              >
                {_.capitalize(sectionName)}
              </Button>
            );
          })}
        </Flex>
      </Flex>
      <Tabs variant="line" colorScheme="black">
        <TabList>
          <Tab
            onClick={() => {
              setSelectedFinancialStatement('balanceSheet');
              setSelectedSectionName('assets');
            }}
          >
            Balance Sheet
          </Tab>
          <Tab
            onClick={() => {
              setSelectedFinancialStatement('incomeStatement');
              setSelectedSectionName('income');
            }}
          >
            Income Statement
          </Tab>
        </TabList>

        <TabPanels>
          {FinancialData.FinancialStatementTypeIterable.map(
            (financialStatement) => (
              <TabPanel
                px="0"
                key={financialStatement}
                onClick={() =>
                  setSelectedFinancialStatement(financialStatement)
                }
              >
                <FinancialStatementsChart
                  financialStatementData={
                    financialStatementQueryResult?.data
                      ?.getFinancialStatementDataByDateRange
                  }
                  {...financialStatementProps}
                />
                <FinancialStatementsTable
                  financialStatementData={
                    financialStatementQueryResult?.data
                      ?.getFinancialStatementDataByDateRange
                  }
                  {...financialStatementProps}
                  queryProps={financialStatementProps}
                />
              </TabPanel>
            ),
          )}
        </TabPanels>
      </Tabs>
    </Flex>
  );
};
